/**
 * Build a Hasura `_bool_exp` from DataTable filter UI state and search input.
 *
 * Translation rules:
 *  - filterType `daterange` -> { [path]: { _gte: 'YYYY-MM-DDT00:00:00', _lte: 'YYYY-MM-DDT23:59:59' } }
 *  - filterType `exact`     -> { [path]: { _eq: value } }
 *                              (array value -> { [path]: { _in: array } })
 *  - filterType `contains`  -> { [path]: { _ilike: '%value%' } }
 *  - filterType `multiselect` (or any array value w/o explicit type)
 *                           -> { [path]: { _in: array } }
 *  - special `tags` field   -> { job_tags: { tag_id: { _in: tagIds } } }
 *  - search (string)        -> _or: [ { f1: { _ilike: '%v%' } }, ... ]
 *
 * Field path resolution:
 *  - Each filterConfig entry may declare an explicit `hasuraField` to map a
 *    transformed/derived JS field name (like `firm_name` produced by a
 *    transformData step) onto the real Hasura column path
 *    (e.g. `contract.firm.firm_name`). When `hasuraField` is omitted, the
 *    raw `field` is used as-is.
 *  - A filterConfig may also declare `transformValue(value)` to convert the
 *    UI value into the Hasura column type before the clause is built
 *    (e.g. 'yes'/'no' select values -> boolean column).
 *  - searchFields entries can be either a string (used as-is) or an object
 *    `{ field: 'displayKey', hasuraField: 'a.b.c' }`. When the entry is a
 *    string and contains a `.`, it is treated as a dotted Hasura path.
 *
 * Nested paths are expanded:
 *    'contract.firm.firm_name' -> { contract: { firm: { firm_name: { _ilike: ... } } } }
 *
 * Empty filters and empty search are skipped. Combines all filter conditions
 * with `_and`. Combines search with the rest using `_and`. Returns `{}`
 * when there are no filters and no search.
 */

const buildPath = (path, leafExpr) => {
  if (!path) return null;
  const parts = path.split('.');
  let acc = leafExpr;
  for (let i = parts.length - 1; i >= 0; i--) {
    acc = { [parts[i]]: acc };
  }
  return acc;
};

const isEmptyValue = value => {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true;
  }
  return false;
};

const escapeLike = value => {
  if (value == null) return '';
  // Escape Hasura/PostgreSQL ILIKE wildcards so user input stays literal.
  return String(value).replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
};

// Turkish-aware fuzzy search: kullanicinin yazdigi her karakter icin Turkce
// eşdegeri veya ASCII eşdegeri kabul edilebilir. Ornek: kullanici "isik"
// yazdiginda "IŞIK TARIM" de eslesir (i↔ı, s↔ş). Regex character class
// karakterleri yerine paylasilir grup kullanilir. `_ilike` yerine
// `_iregex` (case-insensitive Postgres regex) ile match yapilir.
const TR_CHAR_GROUPS = {
  a: '[aâáàAÂÁÀ]', A: '[aâáàAÂÁÀ]',
  c: '[cçCÇ]',    C: '[cçCÇ]',
  g: '[gğGĞ]',    G: '[gğGĞ]',
  i: '[iıîIİÎ]',  I: '[iıîIİÎ]',
  o: '[oöôOÖÔ]',  O: '[oöôOÖÔ]',
  s: '[sşSŞ]',    S: '[sşSŞ]',
  u: '[uüûUÜÛ]',  U: '[uüûUÜÛ]',
  'ı': '[iıîIİÎ]', 'İ': '[iıîIİÎ]',
  'ş': '[sşSŞ]',   'Ş': '[sşSŞ]',
  'ğ': '[gğGĞ]',   'Ğ': '[gğGĞ]',
  'ü': '[uüûUÜÛ]', 'Ü': '[uüûUÜÛ]',
  'ö': '[oöôOÖÔ]', 'Ö': '[oöôOÖÔ]',
  'ç': '[cçCÇ]',   'Ç': '[cçCÇ]',
};

const REGEX_META = /[\\^$.*+?()[\]{}|]/;

const toTurkishFuzzyRegex = (value) => {
  if (value == null) return '';
  const str = String(value);
  let out = '';
  for (const ch of str) {
    if (TR_CHAR_GROUPS[ch]) {
      out += TR_CHAR_GROUPS[ch];
    } else if (REGEX_META.test(ch)) {
      out += `\\${ch}`;
    } else {
      out += ch;
    }
  }
  return out;
};

/**
 * Resolve the Hasura field path for a given filter UI entry.
 * Looks up the matching filterConfig by `field` to discover any
 * `hasuraField` override the page declared.
 */
const resolveFilterPath = (uiField, filterConfigs) => {
  const config = (filterConfigs || []).find(c => c.field === uiField);
  if (config && config.hasuraField) return config.hasuraField;
  return uiField;
};

const resolveFilterType = (uiField, filterConfigs, fallbackValue) => {
  const config = (filterConfigs || []).find(c => c.field === uiField);
  if (config && config.filterType) return config.filterType;
  if (Array.isArray(fallbackValue)) return 'multiselect';
  if (
    fallbackValue &&
    typeof fallbackValue === 'object' &&
    'startDate' in fallbackValue &&
    'endDate' in fallbackValue
  ) {
    return 'daterange';
  }
  return 'exact';
};

const buildFilterClause = (uiField, value, filterConfigs) => {
  if (isEmptyValue(value)) return null;

  // Per-field opt-out: some filters are computed display fields without a
  // clean Hasura column path — let the config tag them so we silently skip.
  const config = (filterConfigs || []).find(c => c.field === uiField);
  if (config && config.noServerFilter) return null;

  // UI değeri ile Hasura kolon tipi ayrışabilir (ör. Select boolean `false`
  // değerini boş sayıyor diye UI 'yes'/'no' string tutar). Config
  // `transformValue(value)` tanımlarsa clause kurulmadan önce uygulanır.
  if (config && typeof config.transformValue === 'function') {
    value = config.transformValue(value);
  }

  // Special tags filter: maps to the job_tags array_relationship.
  if (uiField === 'tags') {
    const ids = Array.isArray(value) ? value : [value];
    const numericIds = ids
      .map(v => {
        const n = parseInt(v, 10);
        return isNaN(n) ? null : n;
      })
      .filter(v => v != null);
    if (numericIds.length === 0) return null;
    return { job_tags: { tag_id: { _in: numericIds } } };
  }

  // Special creator-name filter: `created_by_name` is a JS-side concatenation
  // of user.first_name + user.last_name, so it has no direct column. Translate
  // a free-text input into a nested search over the `user` relation. Tokens
  // are AND'd so "Hakan Bali" matches first_name=Hakan AND last_name=Bali;
  // each token is OR'd across first_name/last_name.
  if (uiField === 'created_by_name') {
    if (typeof value !== 'string' || value.trim() === '') return null;
    const tokens = value.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return null;
    const tokenClauses = tokens.map(token => {
      const escaped = escapeLike(token);
      return {
        _or: [
          { first_name: { _ilike: `%${escaped}%` } },
          { last_name: { _ilike: `%${escaped}%` } },
        ],
      };
    });
    return {
      user: tokenClauses.length === 1 ? tokenClauses[0] : { _and: tokenClauses },
    };
  }

  const path = resolveFilterPath(uiField, filterConfigs);
  if (!path) return null;
  const filterType = resolveFilterType(uiField, filterConfigs, value);

  switch (filterType) {
    case 'daterange': {
      if (!value || !value.startDate || !value.endDate) return null;
      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
      const fmt = d => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const startISO = `${fmt(startDate)}T00:00:00`;
      const endISO = `${fmt(endDate)}T23:59:59`;
      return buildPath(path, { _gte: startISO, _lte: endISO });
    }

    case 'multiselect': {
      const arr = Array.isArray(value) ? value : [value];
      if (arr.length === 0) return null;
      return buildPath(path, { _in: arr });
    }

    case 'contains': {
      const escaped = escapeLike(value);
      return buildPath(path, { _ilike: `%${escaped}%` });
    }

    case 'exact':
    default: {
      if (Array.isArray(value)) {
        if (value.length === 0) return null;
        return buildPath(path, { _in: value });
      }
      return buildPath(path, { _eq: value });
    }
  }
};

const buildSearchClause = (searchValue, searchFields) => {
  if (!searchValue || typeof searchValue !== 'string' || searchValue.trim() === '') {
    return null;
  }
  if (!Array.isArray(searchFields) || searchFields.length === 0) return null;

  const trimmed = searchValue.trim();
  const numericValue = /^-?\d+$/.test(trimmed) ? Number(trimmed) : null;
  // Turkish-aware regex: "isik" yazsa "IŞIK" ile eslesir (i↔ı, s↔ş, ...).
  // `_iregex` case-insensitive Postgres regex. Wrapping olmadan substring
  // match icin regex bir yerde eslesir yeter (anchor'siz).
  const regex = toTurkishFuzzyRegex(trimmed);

  const orClauses = searchFields
    .map(entry => {
      let path = null;
      let type = 'text';
      if (typeof entry === 'string') path = entry;
      else if (entry && typeof entry === 'object') {
        path = entry.hasuraField || entry.field || null;
        type = entry.type || 'text';
      }
      if (!path) return null;
      // Numeric columns (e.g. project_number INTEGER) can't accept _ilike/regex.
      // Skip if input isn't a clean integer; otherwise exact match.
      if (type === 'int' || type === 'integer' || type === 'number') {
        if (numericValue === null) return null;
        return buildPath(path, { _eq: numericValue });
      }
      return buildPath(path, { _iregex: regex });
    })
    .filter(Boolean);

  if (orClauses.length === 0) return null;
  if (orClauses.length === 1) return orClauses[0];
  return { _or: orClauses };
};

/**
 * Main entry: build a Hasura `_bool_exp` for the given UI state.
 *
 * @param {Object} params
 * @param {Object} params.filters       UI filter map: { [uiField]: value }
 * @param {string} params.searchValue   Free-text search input
 * @param {Array}  params.searchFields  searchFields[] from the table config
 * @param {Array}  params.filterConfigs filterConfigs[] from the table config
 * @returns {Object} Hasura `_bool_exp` (may be `{}` if no clauses)
 */
export const buildHasuraWhere = ({
  filters = {},
  searchValue = '',
  searchFields = [],
  filterConfigs = [],
} = {}) => {
  const filterClauses = Object.entries(filters || {})
    .map(([field, value]) => buildFilterClause(field, value, filterConfigs))
    .filter(Boolean);

  const searchClause = buildSearchClause(searchValue, searchFields);

  const allClauses = [...filterClauses];
  if (searchClause) allClauses.push(searchClause);

  if (allClauses.length === 0) return {};
  if (allClauses.length === 1) return allClauses[0];
  return { _and: allClauses };
};

/**
 * Merge a permission `where` (already constructed by the entity hook) with the
 * user-driven filter `where` from the DataTable. Returns the simpler of the two
 * if either is empty.
 */
export const mergeWhereConditions = (permWhere, userWhere) => {
  const hasPerm = permWhere && Object.keys(permWhere).length > 0;
  const hasUser = userWhere && Object.keys(userWhere).length > 0;
  if (!hasPerm && !hasUser) return {};
  if (!hasPerm) return userWhere;
  if (!hasUser) return permWhere;
  return { _and: [permWhere, userWhere] };
};

export default buildHasuraWhere;
